import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  FilterModel, groupBy, groupByValues,
  IdCodeNameQuery, LanguagesQuery, LeModalService, PropertiesQuery,
  simplifiedRegexSearch, TableComponent
} from "common-shared";
import {
  ItemsQuery,
  ItemListQuery,
  ItemGroupsQuery,
  ItemListFilter, ItemQuery
} from "web-shared";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {BehaviorSubject, noop} from 'rxjs';
import {map, takeUntil} from "rxjs/operators";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {NgSelectComponent} from "@ng-select/ng-select";
import {ManufacturingItemEditModalComponent} from '../manufacturing-item-edit-modal/manufacturing-item-edit-modal.component';
import {ItemService} from "../../../common/item/item.service";

@Component({
  selector: 'app-item-picker',
  templateUrl: './item-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host {
      display: flex;
    }
  `],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ItemPickerComponent),
    multi: true
  }]
})
export class ItemPickerComponent extends TableComponent implements ControlValueAccessor {
  @Input() name: string;
  @Input() readonly: boolean = false;
  @Input() showEdit: boolean = true;
  @Input() multiple: boolean = false;
  @Input() placeholder: string = '';
  @Input() emitOnEvent: 'change' | 'close' = 'change';
  @Output() onItemSelect = new EventEmitter<string | string[]>();
  @ContentChild('buttonsTemplate') buttonsTemplate: TemplateRef<any>;
  @ContentChild('tableButtonsTemplate') tableButtonsTemplate: TemplateRef<any>;
  @Input() addTagText: string = 'Add new';
  @Input() allowAdd: boolean = false;
  addTag: ((term: string) => any | Promise<any>) = undefined;
  @Output() onItemAdd = new EventEmitter<string>();

  itemId: string | string[] = '';
  private onTouchedCallback: () => void = noop;
  public onChangeCallback: (_: any) => void = noop;
  filteredData$ = new BehaviorSubject<ItemListQuery[]>([]);
  pickerBsModalRef: NgbModalRef;
  filter = new FilterModel<ItemListFilter>({Data: new ItemListFilter(), Page: this.page, AdvancedFilter: true});
  @ViewChild('selectCmp') ngSelect: NgSelectComponent;
  loading$ = new BehaviorSubject<boolean>(true);
  search$ = new BehaviorSubject<string>(null);
  rowSelectEnabled = true;

  constructor(public itemsQuery: ItemsQuery, public itemService: ItemService, private modalService: LeModalService, public itemGroupsQuery: ItemGroupsQuery, public propertiesQuery: PropertiesQuery, private changeDetectorRef: ChangeDetectorRef, public languagesQuery: LanguagesQuery) {
    super();
  }

  ngOnInit(): void {
    let subscription = this.itemsQuery.selectLoading().pipe(takeUntil(this.componentDestroyed)).subscribe(value => {
      this.updateLoading(value);
    });


    this.itemsQuery.getAll$.pipe(takeUntil(this.componentDestroyed), map(value => value.map(value1 => new ItemListQuery(value1)))).subscribe(value => {
      const batchSize = 100000;
      for (let mapElement of groupByValues(value, obj => obj.Group)) {
        if(mapElement.length > batchSize){
          for (let i = 0; i < mapElement.length; i += batchSize) {
            if(i > 0){
              for (let sliceElement of mapElement.slice(i, i + batchSize)) {
                sliceElement.Group = `${sliceElement.Group} (${i})`;
              }
            }
          }
        }
      }

      this.filteredData$.next(value);
      if (value.length) {
        this.updateLoading(false);
        this.changeDetectorRef.detectChanges();
      }
      subscription.unsubscribe();
    });

    if (this.allowAdd) {
      this.addTag = term => {
        this.onItemAdd.emit(term);
      }
    }
  }

  private updateLoading(value: boolean) {
    this.loading$.next(value);
    this.changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  writeValue(value: string | string[]): void {
    if (value !== this.itemId) {
      this.itemId = value;
    }
  }

  openPicker(templateRef: TemplateRef<any>) {
    this.pickerBsModalRef = this.modalService.bsModalService.open(templateRef, {windowClass: 'modal-full modal-fit-content', backdrop: "static", keyboard: false});
  }

  openSelectForEdit(mode: string) {
    if (mode === 'edit') {
      setTimeout(() => {
        this.ngSelect?.open();
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  onSelectOpen() {
    this.filter.Page.Size = 0;
    this.filter.Data.ForSelect = true;
    this.filter.Data.GroupByDataset = true;
    this.filter.setFilters(this.defaultFilter);
    this.load();
  }

  onSelectClose() {
    if (this.emitOnEvent === 'close') {
      this.onItemSelect.emit(this.itemId);
    }
  }

  private load() {
    setTimeout(() => {
      this.updateLoading(true);
      this.itemService.getByFilter(this.filter).pipe(takeUntil(this.componentDestroyed)).subscribe(pagedData => {
        this.filteredData$.next(pagedData.Data);
        this.updateLoading(false);
      });
    });
  }

  select() {
    if (this.multiple) {
      this.onChangeCallback([...this.itemId]);
    } else {
      this.onChangeCallback(this.itemId);
    }

    if (this.emitOnEvent === 'change') {
      this.onItemSelect.emit(this.itemId);
    }
  }

  searchFn(term: string, item: IdCodeNameQuery): boolean {
    return simplifiedRegexSearch(term, [item.Code, item.Name, item.DisplayName]);
  }

  openItem() {
    let entityId;
    if (this.multiple) {
      entityId = this.itemId[0];
    } else {
      entityId = this.itemId;
    }

    this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: entityId} as ManufacturingItemEditModalComponent, class: 'modal-xl'}).onHide$.pipe(takeUntil(this.componentDestroyed))
      .subscribe((bsModalRef) => {
        const comp = bsModalRef.content as ManufacturingItemEditModalComponent;
        if (comp.entityId) {
          this.itemId = comp.entityId;
        }
      });
  }

  clearAll() {
    if (this.multiple) {
      this.itemId = [];
    } else {
      this.itemId = null;
    }
    this.onChangeCallback(this.itemId);
    this.onItemSelect.emit(this.itemId);
  }

  addNew(searchTerm: string = null) {
    this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityName: searchTerm} as ManufacturingItemEditModalComponent, class: 'modal-xl'}).onHide$.pipe(takeUntil(this.componentDestroyed)).subscribe((bsModalRef) => {
      const comp = bsModalRef.content as ManufacturingItemEditModalComponent;
      if (comp.entityId) {
        this.itemId = comp.entityId;
      }
    });
  }
}
